import React, { createContext, useContext, useReducer } from 'react'
import cuid from 'cuid'

import {
	EArticleSectionTypes,
	EArticleActionTypes,
	IArticle,
	IArticleAction,
	IStoryState,
	EEntity,
} from 'models'

interface StoryInterface {
	initialSection?: EArticleSectionTypes
	story?: IArticle
	reset?: boolean
}

let initialStoryState: IStoryState = {
	id: '',
	author: undefined,
	title: '',
	body: '',
	description: '',
	featuredImage: undefined,
	featuredVideo: '',
	success: {
		publish: false,
	},
	error: {
		title: false,
		featuredImage: false,
		featuredVideo: false,
		body: false,
		postAs: false,
		description: false,
	},
}
const StoryContext: React.Context<any> = createContext(null)
const reducer: React.Reducer<any, any> = (
	state: IStoryState,
	action: IArticleAction,
) => {
	let errorData: Object
	switch (action.type) {
		case EArticleActionTypes.resetState: {
			return { ...state, ...initialStoryState }
		}
		case EArticleActionTypes.setTitle:
			return {
				...state,
				error: {
					...state.error,
					title: action.title ? action.title.trim().length < 1 : true,
				},
				title: action.title,
			}
		case EArticleActionTypes.setFeaturedImage:
			return {
				...state,
				error: {
					...state.error,
					featuredImage: false,
					featuredVideo: false,
				},
				featuredImage: action.featuredImage,
			}
		case EArticleActionTypes.setFeaturedVideo:
			return {
				...state,
				error: {
					...state.error,
					featuredVideo: false,
					featuredImage: false,
				},
				featuredImage: null,
				featuredVideo: action.featuredVideo,
			}

		case EArticleActionTypes.setBody:
			errorData = state.error
			return {
				...state,
				error: {
					...state.error,
					...errorData,
					body: false,
				},
				body: action.body,
			}
		case EArticleActionTypes.setPostAs:
			return {
				...state,
				error: {
					...state.error,
					postAs: false,
				},
				postAs: action.postAs,
				postAsId: action.postAsId,
			}
		case EArticleActionTypes.setError:
			return {
				...state,
				error: {
					...state.error,
					...action.error,
				},
			}
		case EArticleActionTypes.clearMedia:
			return {
				...state,
				error: {
					...state.error,
					featuredVideo: false,
					featuredImage: false,
				},
				featuredImage: null,
				featuredVideo: null,
			}
		default:
			throw Error('Invalid Story action')
	}
}
export const StoryProvider: React.FunctionComponent<StoryInterface> = ({
	children,
	story,
	reset = false,
}) => {
	let state: IStoryState = { ...initialStoryState }
	if (!reset && story) {
		if (story.id) state.id = story.id
		state = {
			...state,
			title: story.title || '',
			body: story.body || '',
			featuredImage: story.featuredImage || undefined,
			featuredVideo: story.featuredVideo || '',
			postAs: story.postAs,
			postAsId:
				story.postAs === EEntity.company
					? story.postingCompany && story.postingCompany.id
					: undefined,
			author: story.author || undefined,
		}
	}
	if (!state.id) {
		state.id = cuid()
	}
	return (
		<StoryContext.Provider value={useReducer(reducer, state)}>
			{children}
		</StoryContext.Provider>
	)
}

interface IStoryStateValue {
	children: any
	keys: any
}

export const StoryStateValue = ({ children, keys = [] }: IStoryStateValue) => {
	let [state, companyStoryDispatch] = useContext(StoryContext)
	const keysMap: any = {}
	keys.map((item: any) => {
		keysMap[item] =
			item.indexOf('|') === -1
				? item.split('.')
				: item.split('|')[0].split('.')
		return undefined
	})
	let result: { [key: string]: any } = []
	keys.map((item: any) => {
		const last =
			item.indexOf('|') === -1
				? [...keysMap[item]].reverse()[0]
				: item.split('|')[1]
		if (typeof keysMap[item] === 'object') {
			result[last] = keysMap[item].reduce(
				(a: any, c: any) => (a !== null ? a[c] : null),
				state,
			)
		} else {
			result[last] = state[keysMap[item]]
		}
		return undefined
	})

	if (keys.indexOf('companyStoryDispatch') !== -1)
		result['companyStoryDispatch'] = companyStoryDispatch

	// TODO: update with a proper interface.
	let elements: any = React.Children.toArray(children)
	if (elements.length === 1) {
		elements = React.cloneElement(elements[0], result)
	} else if (elements.length > 0) {
		for (let i in elements) {
			elements[i] = React.cloneElement(elements[i], result)
		}
	}
	return <>{elements}</>
}

export const useStoryState = () => useContext(StoryContext)
